(function(){
'use strict';
/*
 * Composants de la page produit repris de Shrine, reecrits en clair.
 *
 * PRINCIPE DE SECURITE : aucun de ces composants ne construit sa propre
 * requete /cart/add. La barre collante et le selecteur secondaire agissent
 * uniquement sur le formulaire produit principal, qui reste gere par
 * <product-form> et <product-info> de Shopify. Le tunnel d'achat ne passe
 * jamais par ce fichier.
 */

/* ------------------------------------------------------------------ *
 * Utilitaires : retrouver le formulaire produit principal
 * ------------------------------------------------------------------ */
function mainProductInfo(sectionId) {
  return (
    document.querySelector(`product-info[data-section="${sectionId}"]`) ||
    document.querySelector('product-info')
  );
}

function mainProductForm(sectionId) {
  const info = mainProductInfo(sectionId);
  const scope = info || document;
  return scope.querySelector('product-form form, form[action*="/cart/add"]');
}

/* ------------------------------------------------------------------ *
 * <sticky-atc> : barre d'achat collante
 * ------------------------------------------------------------------ */
class StickyAtc extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.section;
    this.afterScroll = this.dataset.afterScroll === 'true' || this.hasAttribute('data-after-scroll');
    this.isScrollBtn = this.dataset.scrollBtn === 'true' || this.hasAttribute('data-scroll-btn');
    this.destination = this.dataset.scrollDestination;

    this.button = this.querySelector('button, .button');
    if (this.button) this.button.addEventListener('click', (e) => this.onClick(e));

    if (this.afterScroll) {
      this.hide();
      this.watchScroll();
    } else {
      this.show();
    }

    this.syncPicker();
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    window.removeEventListener('scroll', this.scrollHandler);
  }

  show() { this.classList.add('sticky-atc--visible'); this.removeAttribute('hidden'); }
  hide() { this.classList.remove('sticky-atc--visible'); }

  /* apparait une fois le formulaire principal sorti de l'ecran */
  watchScroll() {
    const form = mainProductForm(this.sectionId);
    const target = form || document.querySelector('.product__info-container');

    if (target && 'IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        (entries) => entries.forEach((e) => (e.isIntersecting ? this.hide() : this.show())),
        { rootMargin: '0px 0px -100px 0px' }
      );
      this.observer.observe(target);
      return;
    }
    this.scrollHandler = () => (window.scrollY > 600 ? this.show() : this.hide());
    window.addEventListener('scroll', this.scrollHandler, { passive: true });
    this.scrollHandler();
  }

  /* soit on defile vers le formulaire, soit on le soumet - jamais de requete maison */
  onClick(event) {
    const form = mainProductForm(this.sectionId);

    if (this.isScrollBtn || !form) {
      event.preventDefault();
      const dest = (this.destination && document.querySelector(this.destination)) ||
                   form || document.querySelector('.product__info-container');
      if (dest) dest.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    event.preventDefault();
    const submit = form.querySelector('[type="submit"]');
    if (submit && !submit.disabled) submit.click();
    else form.requestSubmit ? form.requestSubmit() : form.submit();
  }

  /* garde le libelle et le prix alignes sur la variante courante */
  syncPicker() {
    const info = mainProductInfo(this.sectionId);
    if (!info) return;
    const observer = new MutationObserver(() => this.refreshPrice());
    observer.observe(info, { subtree: true, childList: true });
    this.refreshPrice();
  }

  refreshPrice() {
    const info = mainProductInfo(this.sectionId);
    if (!info) return;
    const src = info.querySelector('.price__container, .price');
    const dst = this.querySelector('.sticky-atc__price, [data-sticky-price]');
    if (src && dst && dst.innerHTML !== src.innerHTML) dst.innerHTML = src.innerHTML;
  }
}
if (!customElements.get('sticky-atc')) customElements.define('sticky-atc', StickyAtc);

/* ------------------------------------------------------------------ *
 * <secondary-variant-select> : selecteur de variante secondaire
 * Repercute le choix sur le selecteur principal, qui declenche
 * la mise a jour normale de la page par <product-info>.
 * ------------------------------------------------------------------ */
class SecondaryVariantSelect extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.section ||
      (this.closest('[data-section]') || {}).dataset?.section;

    this.addEventListener('change', (e) => this.relay(e));
  }

  relay(event) {
    const source = event.target;
    if (!source || !source.value) return;

    const info = mainProductInfo(this.sectionId);
    if (!info) return;

    /* cas 1 : le selecteur principal expose directement les variantes */
    const mainSelect = info.querySelector('variant-selects select, select[name="options[]"], select[data-variant-select]');
    if (mainSelect && Array.from(mainSelect.options).some((o) => o.value === source.value)) {
      mainSelect.value = source.value;
      mainSelect.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    /* cas 2 : variantes en boutons radio */
    const radio = info.querySelector(`input[type="radio"][value="${CSS.escape(source.value)}"]`);
    if (radio) {
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    /* cas 3 : variante identifiee par son id -> on relaie sur variant-selects */
    const vs = info.querySelector('variant-selects');
    if (vs) {
      const opt = vs.querySelector(`option[value="${CSS.escape(source.value)}"]`);
      if (opt && opt.parentElement) {
        opt.parentElement.value = source.value;
        opt.parentElement.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }
}
if (!customElements.get('secondary-variant-select'))
  customElements.define('secondary-variant-select', SecondaryVariantSelect);
if (!customElements.get('secondary-variant-select-separate'))
  customElements.define('secondary-variant-select-separate', class extends SecondaryVariantSelect {});

/* ------------------------------------------------------------------ *
 * <volume-pricing> : conteneur de tarifs degressifs (presentation seule)
 * ------------------------------------------------------------------ */
if (!customElements.get('volume-pricing')) {
  customElements.define('volume-pricing', class extends HTMLElement {});
}

})();
